import { BitmapCoordinatesRenderingScope, CanvasRenderingTarget2D } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import {
    ColorValue,
    DrawingSettingGroup,
    DrawingTypes,
    LabelAxisViewConfig,
    Point,
    PointCoordinates,
    SettingValues
} from '../drawing.interface'
import { positionsBox, scaledCoordinate } from '../drawing.helper'
import { PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { SettingType } from '../drawing.enums'
import { primaryDrawingColor, rectangleDrawingColor } from '../../../../assets/colors'

export type RectangleDrawingState = {
    p1?: Point,
    p2?: Point,
    p3?: Point,
    p4?: Point
}

export type RectangleDrawingSettings = {
    fillColor: ColorValue,
    strokeColor: ColorValue,
    strokeWidth: number,
    labelColor: ColorValue,
    point1: Point,
    point2: Point
}

class RectangleDrawingTool extends DrawingBase<any, RectangleDrawingSettings, RectangleDrawingState> {

    draw(scope: BitmapCoordinatesRenderingScope, target: CanvasRenderingTarget2D) {
        const ctx = scope.context
        const { p1, p3 } = this._state
        if (!p1 || !p3) return

        const p1x = this.timeToCoordinate(p1.x)
        const p3x = this.timeToCoordinate(p3.x)
        if (!p1x || !p3x) return
        const horizontalPositions = positionsBox(
            p1x,
            p3x,
            scope.horizontalPixelRatio
        )
        const p1y = this.priceToCoordinate(p1.y)
        const p3y = this.priceToCoordinate(p3.y)
        if (!p1y || !p3y) return
        const verticalPositions = positionsBox(
            p1y,
            p3y,
            scope.verticalPixelRatio
        )
        ctx.fillStyle = this._settings.fillColor.color
        ctx.globalAlpha = this._settings.fillColor.opacity
        ctx.fillRect(
            horizontalPositions.position,
            verticalPositions.position,
            horizontalPositions.length,
            verticalPositions.length
        )
        let topLeftX = Math.min(p1x, p3x), topLeftY = Math.min(p1y, p3y), width = Math.abs(p1x - p3x),
            height = Math.abs(p1y - p3y)
        topLeftX = scaledCoordinate(topLeftX, scope.horizontalPixelRatio)
        topLeftY = scaledCoordinate(topLeftY, scope.verticalPixelRatio)
        width *= scope.horizontalPixelRatio
        height *= scope.verticalPixelRatio
        ctx.strokeStyle = this._settings.strokeColor.color
        ctx.globalAlpha = this._settings.strokeColor.opacity
        ctx.lineWidth = this._settings.strokeWidth * window.devicePixelRatio
        ctx.strokeRect(topLeftX, topLeftY, width, height)

    };

    onAddPoint = (time: Time, price: number, coordinates: PointCoordinates, preview?: boolean) => {
        const { p1, p3 } = this._state
        const point: Point = {
            id: 'p1',
            x: time,
            y: price
        }
        if (!p1) {
            this._state.p1 = point
        } else if (!p3 || preview) {
            point.id = 'p3'
            this._state.p3 = point
        }
        p3 && !preview && this.onDrawingEnd()
        if (!preview) return { ...point }
    }
    getHandles = (): Point[] => {
        const handles: Point[] = []
        const { p1, p2, p3, p4 } = this._state
        if (p1) {
            handles.push(p1)
        }

        if (p2) {
            handles.push(p2)
            handles.push(p3!)
            handles.push(p4!)
        }

        return handles
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const { p1, p3 } = this._state
        if (!p1 || !p3) return null
        const p1x = this.timeToCoordinate(p1.x)
        const p3x = this.timeToCoordinate(p3.x)
        const p1y = this.priceToCoordinate(p1.y)
        const p3y = this.priceToCoordinate(p3.y)
        if (!p1x || !p3x || !p1y || !p3y) return null
        let minX = p1x, maxX = p3x, minY = p1y, maxY = p3y

        if (p3x < p1x) {
            minX = p3x
            maxX = p1x
        }

        if (p3y < p1y) {
            minY = p3y
            maxY = p1y
        }

        if (x >= minX && x <= maxX && y >= minY && y <= maxY) {
            return {
                externalId: this.id,
                zOrder: 'normal'
            }
        }
        return null
    }

    updateHandles(points: Point[]) {
        points.forEach((h) => {
            switch (h.id) {
                case 'p1':
                    this._state.p1 = h
                    break
                case 'p2':
                    this._state.p2 = h
                    break
                case 'p3':
                    this._state.p3 = h
                    break
                case 'p4':
                    this._state.p4 = h
                    break

            }
        })
    }

    updateHandle = (id: string, time: Time, price: number) => {
        const { p1, p2, p3, p4 } = this._state
        if (!p1 || !p2 || !p3 || !p4) return
        switch (id) {
            case 'p1':
                p1.x = time
                p1.y = price
                break
            case 'p2':
                p1.x = time
                p3.y = price
                break
            case 'p3':
                p3.x = time
                p3.y = price
                break
            case 'p4':
                p1.y = price
                p3.x = time
                break
        }
    }
    recalculateHandles = () => {
        const { p1, p3 } = this._state

        if (!p1 || !p3) return

        this._state.p2 = {
            id: 'p2',
            x: p1.x,
            y: p3.y
        }
        this._state.p4 = {
            id: 'p4',
            y: p1!.y,
            x: p3.x
        }
    }
    getPriceAxisLabelPoints = (): LabelAxisViewConfig<number>[] => {
        const { p1, p3 } = this._state
        if (!p1 || !p3) return []
        const min = Math.min(p1.y, p3.y)
        const max = Math.max(p1.y, p3.y)
        if (!min || !max) return []
        return [{
            id: 'price',
            p1: min,
            p2: max,
            fill: true,
            fillColor: this._settings.fillColor.color,
            labelColors: {
                p1: this._settings.labelColor.color,
                p2: this._settings.labelColor.color
            }
        }]
    }

    getTimeAxisLabelPoints = (): LabelAxisViewConfig<Time>[] => {
        const { p1, p3 } = this._state
        if (!p1 || !p3) return []
        const p1x = this.timeToCoordinate(p1.x)
        const p3x = this.timeToCoordinate(p3.x)

        if (!p1x || !p3x) return []
        let min: Time = p1.x, max: Time = p3.x
        if (p1x > p3x) {
            min = p3.x
            max = p1.x
        }
        if (!min || !max) return []

        return [{
            id: 'time',
            p1: min,
            p2: max,
            fill: true,
            fillColor: this._settings.fillColor.color,
            labelColors: {
                p1: this._settings.labelColor.color,
                p2: this._settings.labelColor.color
            }
        }]
    }

    onSettingsUpdate = (settings: Partial<RectangleDrawingSettings>) => {
        if (settings.point1) {
            this._state.p1 = settings.point1
        }
        if (settings.point2) {
            this._state.p3 = settings.point2
        }
    }

    getAvailableSettings = (): DrawingSettingGroup<RectangleDrawingSettings, SettingValues>[] => {
        return [{
            id: 'style',
            displayName: 'Style',
            visible: true,
            settings: [
                {
                    name: 'fillColor',
                    label: 'Fill Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: rectangleDrawingColor,
                        opacity: 0.2
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'labelColor',
                    label: 'Label Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: primaryDrawingColor,
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'strokeColor',
                    label: 'Border Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: rectangleDrawingColor,
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'strokeWidth',
                    label: 'Border Width',
                    type: SettingType.STROKE_WIDTH,
                    defaultValue: 1,
                    custom: true,
                    visible: true
                }
            ]
        }, {
            id: 'coordinates',
            displayName: 'Coordinates',
            visible: true,
            settings: [
                {
                    name: 'point1',
                    label: '#1 (price, bar)',
                    type: SettingType.COORDINATE,
                    defaultValue: {
                        id: 'p1',
                        x: 0 as Time,
                        y: 0
                    },
                    custom: true,
                    visible: true,
                    fieldOptions: {
                        showX: true,
                        showY: true
                    }
                },
                {
                    name: 'point2',
                    label: '#2 (price, bar)',
                    type: SettingType.COORDINATE,
                    defaultValue: {
                        id: 'p3',
                        x: 0 as Time,
                        y: 0
                    },
                    custom: true,
                    visible: true,
                    fieldOptions: {
                        showX: true,
                        showY: true
                    }
                }
            ]
        }]
    }

    getSettingsValues(): RectangleDrawingSettings {
        const settings = { ...this._settings }
        if (this._state.p1) {
            settings.point1 = this._state.p1
        }
        if (this._state.p3) {
            settings.point2 = this._state.p3
        }
        return settings
    }

    getMiniSettings = () => {
        const settings = this.getAvailableSettings()
        let miniSettings: (keyof RectangleDrawingSettings)[] = []

        settings.forEach((s) => {
            if (s.id === 'style') {
                miniSettings = [...s.settings.map(setting => setting.name).filter(v => v !== 'labelColor')]
            }
        })

        return miniSettings
    }

    getDrawingType(): DrawingTypes {
        return 'rectangle'
    }
}

export default RectangleDrawingTool